import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Table, Form, Typography } from 'antd';
import moment from 'moment';
import i18n from 'i18next';
import { Trans } from 'react-i18next';

import {
	downloadTaxStatement,
	getTaxStatements,
	generateTaxStatement,
} from 'features/statement/actions';

import { RangeDatePicker } from 'componets/common/RangeDatePicker';
import { FormItem } from 'componets/form';
import { RejectedMessage, TableWrapper } from 'componets/ui';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { IRangePicker } from 'models';

import { RequestStatus, Rules } from 'utils/constants';

import { selectUser } from 'selectors';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as UpdateIcon } from 'assets/icons/update.svg';

import s from '../index.module.scss';

let interval: any = null;

const AXIOS_TIMEOUT = 3000;

const { t, language } = i18n;

const columns = [
	{
		title: t('statementPeriodText'),
		dataIndex: 'timespan',
	},
	{
		title: '',
		dataIndex: 'id',
		render: (id: number, row: any) => {
			return (
				<TaxStatementsItemDownload
					id={id}
					timespan={row.timespan}
					statement_status={row.statement_status}
				/>
			);
		},
	},
];

export const TaxStatements: FC = () => {
	const { taxStatements, status: requestStatus } = useAppSelector((state) => state.statement);
	const user = useAppSelector(selectUser);

	const { generateStatementText } = useTranslate(['generateStatementText']);

	const [range, setRange] = useState<IRangePicker>({} as IRangePicker);

	const dispatch = useAppDispatch();

	const isGeneratingStatement = useMemo(
		() => taxStatements.find((item) => !item.statement_status),
		[taxStatements],
	);

	useEffect(() => {
		if (isGeneratingStatement) {
			interval = setTimeout(() => {
				dispatch(getTaxStatements({}));
			}, AXIOS_TIMEOUT);
		}
	}, [dispatch, isGeneratingStatement]);

	useEffect(() => {
		dispatch(getTaxStatements({}));
	}, [dispatch]);

	useEffect(() => {
		return () => clearTimeout(interval);
	}, []);

	const onGenerateTaxStatement = () => {
		if (range && range.startDate) {
			const data = {
				lang: language,
				startDate: range && moment(range.startDate).format('YYYY-MM-DD'),
				endDate: range && moment(range.endDate).format('YYYY-MM-DD'),
			};

			dispatch(generateTaxStatement(data));
		}
	};

	if (requestStatus === RequestStatus.REJECTED) {
		return <RejectedMessage />;
	}

	return (
		<div>
			<div className={s.filters}>
				<Trans
					i18nKey="generateStatementCurrentText"
					components={{
						btn: (
							<div
								role="button"
								className={`${s.download} white-btn`}
								onClick={() => {
									dispatch(
										generateTaxStatement({
											lang: language,
											startDate: moment().startOf('year').format('YYYY-MM-DD'),
											endDate: moment().format('YYYY-MM-DD'),
										}),
									);
								}}
							/>
						),
						icon: <UpdateIcon />,
						text: <Typography.Text className={s.generateText} />,
						filter: <div className={s.filterText} />,
						textFilter: <Typography.Text type="secondary" />,
					}}
				/>

				<Form className={s.rangeFilter} onFinish={onGenerateTaxStatement}>
					<FormItem className={s.rangeInput} name="range" rules={[Rules.REQUIRED_SELECT]}>
						<RangeDatePicker
							onChange={setRange}
							range={range}
							minDate={moment(user.registrationDate).startOf('year')}
						/>
					</FormItem>

					<Button className={`${s.generate} white-btn`} htmlType="submit">
						<UpdateIcon />
						{generateStatementText}
					</Button>
				</Form>
			</div>

			<TableWrapper
				data={taxStatements}
				loading={requestStatus === RequestStatus.PENDING && taxStatements.length === 0}
			>
				<Table
					className="accountTable statementTable"
					pagination={false}
					columns={columns}
					dataSource={taxStatements}
				/>
			</TableWrapper>
		</div>
	);
};

type TaxStatementsItemDownloadProps = {
	id: number;
	timespan: string;
	statement_status: boolean;
};

const TaxStatementsItemDownload: FC<TaxStatementsItemDownloadProps> = ({
	id,
	timespan,
	statement_status,
}) => {
	const dispatch = useAppDispatch();

	const { downloadText, generatingText, taxStatementText } = useTranslate([
		'downloadText',
		'generatingText',
		'taxStatementText',
	]);

	const onDownload = () => {
		dispatch(downloadTaxStatement({ id, fileName: `${taxStatementText} ${timespan}.pdf` }));
	};

	return (
		<Button
			loading={!statement_status}
			onClick={onDownload}
			className={s.tableDownload}
			disabled={!statement_status}
		>
			{statement_status && <DownloadIcon />}
			<span className={s.btnText}>{statement_status ? downloadText : generatingText}</span>
		</Button>
	);
};
